/**
 * Pipeline for streaming AI responses to the client.
 *
 * This pipeline:
 * 1. Tracks tool outputs for widget denormalization
 * 2. Transforms widget tool responses into WidgetParts
 * 3. Strips extra fields for cleaner client data
 * 4. Converts remaining DataParts to text (fallback), but preserves attachment-like
 *    DataParts (hotel, destination, etc.) so they stay visible as attachments in the UI
 * 5. Filters out empty text parts (Genkit can return { text: "" } chunks)
 */

import type { CoreLogger } from '@elara/core/logger';

import type { PartProcessor } from '../parts/index.js';
import type { StreamingPipelineOptions } from './StreamingPipeline.js';

import { isAnsweredQuestionsPart, isAttachmentDataPart } from '#models/ChatMessageModelSpec.js';

import { StreamingPipeline } from './StreamingPipeline.js';

import {
  ChainPartProcessor,
  DataPartToTextProcessor,
  FilterEmptyPartsProcessor,
  StripExtraFieldsProcessor,
  ToolOutputTrackerProcessor,
  WidgetTransformProcessor,
} from '../parts/index.js';

export class ModelToClientPipeline extends StreamingPipeline {
  readonly partProcessor: PartProcessor;
  readonly toolOutputTracker: ToolOutputTrackerProcessor;
  readonly pipelineId = 'model-to-client' as const;

  /**
   * @param baseLogger - Logger instance
   * @param toolTracker - Optional shared tracker. When provided, uses this tracker instead of
   *                      creating a new one. This allows sharing tracker state with other
   *                      pipelines (e.g., FirestoreToModelPipeline) for cross-turn dataRef
   *                      resolution.
   */
  constructor(baseLogger: CoreLogger, toolTracker?: ToolOutputTrackerProcessor, options?: StreamingPipelineOptions) {
    super(baseLogger, options);
    this.toolOutputTracker = toolTracker ?? new ToolOutputTrackerProcessor(baseLogger);
    this.widgetInterleaver?.setRecommendationTypeResolver((entityId) =>
      this.toolOutputTracker.getRecommendationType(entityId),
    );
    this.partProcessor = ChainPartProcessor.from(
      [
        this.toolOutputTracker,
        new WidgetTransformProcessor(this.toolOutputTracker, baseLogger),
        new StripExtraFieldsProcessor(baseLogger),
        new DataPartToTextProcessor(
          { format: 'structured', skip: (part) => isAnsweredQuestionsPart(part) || isAttachmentDataPart(part) },
          baseLogger,
        ),
        new FilterEmptyPartsProcessor(baseLogger, false),
      ],
      baseLogger,
    );
  }

  /**
   * Resets the pipeline state for a new streaming session.
   */
  reset(): void {
    this.toolOutputTracker.reset();
  }
}
